import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loanAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { formatCurrency } from '../../utils/currency';
import { Plus, FileText, Clock, CheckCircle, XCircle, DollarSign, ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { useDialog } from '../../components/Dialog';
import './LoanListPage.css';

const LoanListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const { showConfirm, showSuccess, showError, DialogComponent } = useDialog();
  const [tab, setTab] = useState('submitted'); // 'submitted' or 'draft'
  const [loans, setLoans] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoans();
  }, [location.pathname]); // Refetch when navigating to this page

  const fetchLoans = async () => {
    try {
      const [loansRes, draftsRes] = await Promise.all([
        loanAPI.getAll({ isDraft: false }),
        loanAPI.getDrafts(),
      ]);

      setLoans(loansRes.data?.loans || loansRes.loans || []);
      setDrafts(draftsRes.data?.drafts || draftsRes.drafts || []);
    } catch (error) {
      console.error('Failed to fetch loans:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: t('pending'), icon: Clock, color: 'warning' },
      approved: { label: t('approved'), icon: CheckCircle, color: 'info' },
      rejected: { label: t('rejected'), icon: XCircle, color: 'danger' },
      disbursed: { label: t('disbursed'), icon: DollarSign, color: 'success' },
      repaying: { label: t('repaying'), icon: DollarSign, color: 'primary' },
      completed: { label: t('completed'), icon: CheckCircle, color: 'success' },
    };

    const config = statusMap[status] || statusMap.pending;
    const Icon = config.icon;

    return (
      <span className={`status-badge ${config.color}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'vi' ? 'vi-VN' : language === 'ko' ? 'ko-KR' : 'en-US');
  };

  const handleContinueDraft = (draftId, e) => {
    e.stopPropagation();
    navigate(`/loan/edit/${draftId}`);
  };

  const handleDeleteDraft = (draftId, e) => {
    e.stopPropagation();

    showConfirm(
      t('deleteDraftMessage') || t('confirmDeleteDraft'),
      async () => {
        try {
          await loanAPI.deleteDraft(draftId);
          showSuccess(t('draftDeletedSuccessfully'));
          fetchLoans(); // Refresh list
        } catch (error) {
          console.error('Failed to delete draft:', error);
          showError(t('cannotDeleteDraft'));
        }
      },
      t('confirmDeleteDraft')
    );
  };

  const handleLoanClick = (loan) => {
    if (tab === 'draft') {
      // For drafts, don't navigate, show actions instead
      return;
    }
    navigate(`/loan/detail/${loan.id}`);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  const currentList = tab === 'submitted' ? loans : drafts;

  return (
    <div className="loan-list-page">
      <DialogComponent />
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/work')}>
          <ArrowLeft size={20} />
        </button>
        <h1 className="page-title">{t('loanRecords')}</h1>
      </div>

      {/* Floating Action Button */}
      <button
        className="fab"
        onClick={() => navigate('/loan/new')}
        aria-label={t('createNew')}
      >
        <Plus size={24} />
      </button>

      <div className="page-content">
        {/* Tabs */}
        <div className="tabs">
          <button
            className={`tab ${tab === 'submitted' ? 'active' : ''}`}
            onClick={() => setTab('submitted')}
          >
            <FileText size={18} />
            {t('submitted')} ({loans.length})
          </button>
          <button
            className={`tab ${tab === 'draft' ? 'active' : ''}`}
            onClick={() => setTab('draft')}
          >
            <Clock size={18} />
            {t('draft')} ({drafts.length})
          </button>
        </div>

        {/* Loan List */}
        {currentList.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} />
            <p>
              {tab === 'submitted'
                ? t('noLoansSubmitted')
                : t('noDrafts')}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/loan/new')}
            >
              <Plus size={20} />
              {t('createNewLoan')}
            </button>
          </div>
        ) : (
          <div className="loan-list">
            {currentList.map((loan) => (
              <div
                key={loan.id}
                className={`loan-card ${tab === 'draft' ? 'draft-card' : ''}`}
                onClick={() => handleLoanClick(loan)}
              >
                <div className="loan-header">
                  <div className="loan-code">{loan.code || `${t('loanCode')} #${loan.id}`}</div>
                  {loan.status && getStatusBadge(loan.status)}
                </div>

                <div className="loan-customer">
                  <strong>{loan.customerName}</strong>
                </div>

                <div className="loan-details">
                  <div className="loan-detail-item">
                    <span className="label">{t('loanAmount')}:</span>
                    <span className="value">{formatCurrency(loan.amount)}</span>
                  </div>

                  <div className="loan-detail-item">
                    <span className="label">{t('loanTerm')}:</span>
                    <span className="value">{loan.termMonths} {t('months')}</span>
                  </div>

                  <div className="loan-detail-item">
                    <span className="label">{t('bank')}:</span>
                    <span className="value">{loan.bankName || 'N/A'}</span>
                  </div>
                </div>

                <div className="loan-footer">
                  <span className="loan-date">{formatDate(loan.createdAt)}</span>
                  {loan.rejectionReason && (
                    <span className="rejection-reason">
                      {t('rejectionReason')}: {loan.rejectionReason}
                    </span>
                  )}
                </div>

                {/* Draft Actions */}
                {tab === 'draft' && (
                  <div className="draft-actions">
                    <button
                      className="btn-draft-action btn-continue"
                      onClick={(e) => handleContinueDraft(loan.id, e)}
                    >
                      <Edit size={16} />
                      {t('continue')}
                    </button>
                    <button
                      className="btn-draft-action btn-delete"
                      onClick={(e) => handleDeleteDraft(loan.id, e)}
                    >
                      <Trash2 size={16} />
                      {t('delete')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanListPage;
